import React from 'react';
import PropTypes from 'prop-types';
import s from './HomeFooter.module.scss';
import { t } from '../../i18n';
import Link from 'next/link';
import Icon from '../Icon';
import classNames from 'classnames';

const mediaUrl = process.env.NEXT_PUBLIC_MEDIA_URL;

const isAbsoluteUrl = (url) => /^https?:\/\//.test(url);

const getFullAddress = (address) => {
    const { streetAddress, postalCode, city } = address || {};
    return [streetAddress, postalCode, city].filter(Boolean).join(', ');
};

const getGoogleMapsUrl = (address) => {
    const fullAddress = getFullAddress(address);
    return fullAddress
        ? `https://www.google.com/maps/place/${encodeURIComponent(fullAddress)}`
        : null;
};

const HomeFooter = ({
    contactEmail = '',
    address = {},
    footerLinks = [],
    video = null,
    videoMp4 = null,
    animatedLogo = null,
    animatedLogoMp4 = null,
}) => {
    const googleMapsUrl = getGoogleMapsUrl(address);
    const resolvedVideoSrc = isAbsoluteUrl(video)
        ? video
        : `${mediaUrl}${video}`;
    const resolvedVideoMp4Src = isAbsoluteUrl(videoMp4)
        ? videoMp4
        : `${mediaUrl}${videoMp4}`;
    const resolvedAnimatedLogoSrc = isAbsoluteUrl(animatedLogo)
        ? animatedLogo
        : `${mediaUrl}${animatedLogo}`;
    const resolvedAnimatedLogoMp4Src = isAbsoluteUrl(animatedLogoMp4)
        ? animatedLogoMp4
        : `${mediaUrl}${animatedLogoMp4}`;

    let animatedLogoSrcs = [];

    if (animatedLogoMp4) {
        animatedLogoSrcs.push({
            src: resolvedAnimatedLogoMp4Src,
            type: 'video/mp4; codecs="hvc1"',
        });
    }

    if (animatedLogo) {
        animatedLogoSrcs.push({
            src: resolvedAnimatedLogoSrc,
            type: 'video/webm',
        });
    }

    let videoSrcs = [];

    if (videoMp4) {
        videoSrcs.push({
            src: resolvedVideoMp4Src,
            type: 'video/mp4; codecs="hvc1"',
        });
    }

    if (video) {
        videoSrcs.push({
            src: resolvedVideoSrc,
            type: 'video/webm',
        });
    }

    return (
        <div className={s.Root}>
            <div className={s.Container}>
                <div className={s.Content}>
                    <div className={s.Column}>
                        <div className={s.EmailWrapper}>
                            <label className={s.Label}>
                                {t('homeFooter.interested')}
                            </label>
                            <div className={s.EmailLinkWrapper}>
                                <span className={s.Label}>
                                    {t('homeFooter.connect')}
                                </span>
                                {contactEmail && (
                                    <Link
                                        href={`mailto:${contactEmail}`}
                                        className={s.Link}>
                                        <span className={s.LinkTitle}>
                                            {contactEmail}
                                        </span>
                                        <Icon
                                            name="ArrowExternalPink"
                                            className={s.Arrow}
                                        />
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={classNames(s.Column, s.Center)}>
                        <div className={s.Logo}>
                            <div className={s.LogoWrapper}>
                                <video
                                    className={s.Video}
                                    autoPlay
                                    loop
                                    muted
                                    playsInline>
                                    {animatedLogoSrcs.map((s) => (
                                        <source
                                            key={s.src}
                                            src={s.src}
                                            type={s.type}
                                        />
                                    ))}
                                </video>
                            </div>
                            <div className={s.LogoNameWrapper}>
                                <Icon
                                    name="LogoOnlyName"
                                    className={s.LogoIcon}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={classNames(s.Column, s.Last)}>
                        <div className={s.LinksAddressWrapper}>
                            <address className={s.Address}>
                                <label className={classNames(s.Label, s.Wide)}>
                                    {t('general.address')}
                                </label>
                                <div className={s.AddressLinkList}>
                                    {googleMapsUrl && address.streetAddress && (
                                        <Link
                                            href={googleMapsUrl}
                                            className={s.Link}
                                            target="_blank"
                                            rel="noopener noreferrer">
                                            <span className={s.LinkTitle}>
                                                {address.streetAddress}
                                            </span>
                                            <Icon
                                                name="ArrowExternalPink"
                                                className={s.Arrow}
                                            />
                                        </Link>
                                    )}
                                    {(address.postalCode || address.city) && (
                                        <span className={s.AddressText}>
                                            {[address.postalCode, address.city]
                                                .filter(Boolean)
                                                .join(', ')}
                                        </span>
                                    )}
                                </div>
                            </address>

                            {footerLinks.length > 0 && (
                                <div className={s.LinksWrapper}>
                                    <label
                                        className={classNames(s.Label, s.Wide)}>
                                        {t('general.links')}
                                    </label>
                                    <div className={s.Links}>
                                        {footerLinks.map((link, index) => (
                                            <Link
                                                key={index}
                                                href={link.url}
                                                className={s.Link}>
                                                <span>{link.title}</span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={s.VideoContainer}>
                    <video
                        className={s.VideoEarth}
                        autoPlay
                        loop
                        muted
                        playsInline>
                        {videoSrcs.map((s) => (
                            <source key={s.src} src={s.src} type={s.type} />
                        ))}
                    </video>
                    <div className={s.Overlay} />
                </div>
            </div>
        </div>
    );
};

HomeFooter.propTypes = {
    contactEmail: PropTypes.string,
    address: PropTypes.shape({
        streetAddress: PropTypes.string,
        postalCode: PropTypes.string,
        city: PropTypes.string,
    }),
    footerLinks: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            url: PropTypes.string,
        })
    ),
};

export default HomeFooter;
